import cx from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import qs from 'qs';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { safeParseInt } from '../../../../../../shared/common/utils';
import { Col } from '../../../../../../shared/frontend/components/grid/Col';
import { Row } from '../../../../../../shared/frontend/components/grid/Row';
import { MediaV2 } from '../../../../../../shared/frontend/components/presentational/media';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';
import { RAID_METADATA_BY_SLUG } from '../../../../shared/enums';
import { RouteComponentProps } from '../../../../shared/types/common';
import { TEST_PAYLOAD } from '../../../fixtures/widgets/versus-widget-data';
import { apiServiceAsync } from '../../services/apiService';
import { useWidgetVersion } from '../hooks/useWidgetVersion';
import { VersusWidgetSingleGuild } from './versusWidgetSingleGuild';

/* tslint:disable:max-line-length */

type TVersusWidgetProps = {
  onGuildOrderChange?: (guildIds: string[]) => void;
  queryOverride?: string;
};

export type TStreamWidgetVersusProps = RouteComponentProps<TVersusWidgetProps, any>;

// let refreshCount = 0;

/**
 * StreamWidgetVersusPage
 */
function StreamWidgetVersusPage(props: TStreamWidgetVersusProps) {
  const location = useLocation();

  const [data, setData] = useState(null as any);
  useWidgetVersion(data?.widgetVersion);
  const qParams = qs.parse(props.queryOverride || location.search, { ignoreQueryPrefix: true });

  const isTestMode = safeParseInt((qParams as any).test, 0);
  if (isTestMode) {
    Object.assign(qParams as any, { raid: TEST_PAYLOAD.guilds[0].raid.slug });
  }

  let simulateTime: null | moment.Moment = null;

  const forceName = useMemo(() => {
    if (qParams.force_name) {
      return (qParams.force_name as string).split(',').reduce(
        (acc, val) => {
          const [guildId, guildName] = val.split(':');
          acc[guildId] = guildName;

          return acc;
        },
        {} as { [key: string]: string }
      );
    }

    return {};
  }, [qParams]);

  useEffect(() => {
    const baseRefreshMs = 1000 * _.clamp(qParams.refresh ? safeParseInt(qParams.refresh as string, 5) : 5, 0, 300);
    let timeoutHandle: any = null;

    const raid = RAID_METADATA_BY_SLUG[qParams.raid as string];

    const isSimulating = safeParseInt(qParams.simulate as string, 0) !== 0;

    function processGuildResponse(response: any) {
      let earliestTime: any = null;
      let latestTime: any = null;

      const allBosses = [response].filter((b: any) => {
        if (isSimulating && simulateTime) {
          const pullTime = moment.utc(b.pullStartedAt);
          if (pullTime > simulateTime) {
            // not time to show this boss kill yet
            return false;
          }
        }

        return true;
      });

      for (const boss of allBosses) {
        if (boss.pullStartedAt && boss.isDefeated) {
          const time = moment.utc(boss.pullStartedAt);
          boss.pullStartedAt = time; // store as a moment rather than string so we do not need to convert it everywhere
          if (!earliestTime || time < earliestTime) {
            earliestTime = time;
          }
          if (!latestTime || time > latestTime) {
            latestTime = time;
          }
        }
      }

      const encountersDefeated = allBosses.filter((p: any) => p.isDefeated);
      const inProgressBosses = allBosses
        .filter((p: any) => p.pullStartedAt)
        .sort((a: any, b: any) => moment.utc(b.pullStartedAt).diff(moment.utc(a.pullStartedAt)));

      let latestEncounter = qParams.boss ? allBosses.find((b: any) => b.boss.slug === qParams.boss) : null;
      if (!latestEncounter) {
        latestEncounter = inProgressBosses.length > 0 ? inProgressBosses[0] : allBosses[allBosses.length - 1];
      }

      const isKilled = latestEncounter?.isDefeated || false;
      const pullCount = latestEncounter?.pullCount || 0;
      const bestPercent = latestEncounter?.bestPercent || 100;
      const progressDisplay = latestEncounter?.progress_display || `${bestPercent.toFixed(1)}%`;

      if (forceName && response.guild.id && forceName[response.guild.id]) {
        response.guild.name = forceName[response.guild.id];
      }

      return {
        raid,
        guild: response.guild,
        allBosses,
        encountersDefeated,
        inProgressBosses,
        latestEncounter,
        pullCount,
        isKilled,
        bestPercent,
        progressDisplay,
        earliestTime,
        latestTime
      };
    }

    async function refreshData() {
      // ++refreshCount;

      if (isTestMode) {
        // we can use stub data here for just seeing how the widget looks
        setData({ ...TEST_PAYLOAD, guilds: TEST_PAYLOAD.guilds.map(processGuildResponse) });
        return;
      }

      const guildIds = ((qParams.guild_ids as string) || '').split(',');

      const guildResponses = [];
      let error = false;

      for (const guildId of guildIds) {
        try {
          const response = await apiServiceAsync.viewLiveTrackingBossProgress({
            guild_id: guildId,
            ..._.pick(qParams, 'raid', 'boss', 'difficulty', 'access_key', 'period')
          });

          guildResponses.push(response);
        } catch (err) {
          console.error({ err }, 'Error caught retrieving boss progress');
          error = true;
        }
      }

      const refreshTimeMs = isSimulating ? 100 : baseRefreshMs;

      if (error && guildResponses.length < 2) {
        // need to at least load the data once
        timeoutHandle = setTimeout(refreshData, refreshTimeMs);
        return;
      }

      if (isSimulating) {
        if (!simulateTime) {
          const lowestTimeUnix = _.min<number>(
            _.compact(
              guildResponses.map((gr) => {
                if (!gr.bosses) {
                  return null;
                }
                return moment.utc(gr.bosses[0].pullStartedAt).unix();
              })
            )
          );
          if (lowestTimeUnix) {
            simulateTime = moment.unix(lowestTimeUnix).subtract(144, 'hours');
          }
        } else {
          // advance the simulation time
          simulateTime = simulateTime.add(6, 'hours');
        }
      }

      const widgetVersion = _.min(guildResponses.map((gr) => gr?.widgetVersion || -1));

      setData({
        guilds: guildResponses.map(processGuildResponse).sort((a: any, b: any) => {
          const aKilledNum = a.isKilled ? 1 : 0;
          const bKilledNum = b.isKilled ? 1 : 0;
          if (aKilledNum === bKilledNum) {
            if (aKilledNum) {
              // was killed, so winner is who killed it first
              return moment.utc(a.latestTime).diff(moment.utc(b.latestTime));
            }

            // was not killed, so winner who is at lowest %
            return a.bestPercent - b.bestPercent;
          } else {
            // only one guild has killed, so they are ahead
            return bKilledNum - aKilledNum;
          }
        }),
        numEncounters: raid.numEncounters,
        widgetVersion
      });

      if (refreshTimeMs > 0) {
        timeoutHandle = setTimeout(refreshData, refreshTimeMs);
      }
    }

    refreshData();

    return () => {
      clearTimeout(timeoutHandle);
    };
  }, []);

  useEffect(() => {
    if (!props.onGuildOrderChange || !data?.guilds) {
      return;
    }

    props.onGuildOrderChange(
      data.guilds
        .map((guild: any) => String(guild?.guild?.id))
        .filter((guildId: string) => guildId !== 'undefined' && guildId !== 'null')
    );
  }, [data, props.onGuildOrderChange]);

  if (!data) {
    return null;
  }

  // if (data.guilds.length !== 2) {
  //   // wrong number of guilds supplied
  //   return null;
  // }

  const forceWrap = safeParseInt((qParams as any).wrap, 0) !== 0;
  const overrideSide = forceWrap ? 'center' : '';

  const hidePulls = safeParseInt(qParams.hide_pulls as string, 0) !== 0;
  const hidePercents = safeParseInt(qParams.hide_percents as string, 0) !== 0;

  const getItems = (itemsPerRow: number) => {
    const parts: any[] = [];
    const widgets: ReactNode[] = [];

    data.guilds.forEach((guild: any, index: number) => {
      widgets.push(
        <VersusWidgetSingleGuild
          key={`${index}_guild`}
          {...guild}
          noPadding
          totalGuilds={data.guilds.length}
          theme={qParams.theme || ''}
          numEncounters={data.numEncounters}
          side={overrideSide || 'right'}
          lifelines={
            qParams[`guild${index}_lifelines`] ? (qParams[`guild${index}_lifelines`] as string).split(',') : []
          }
          hidePulls={hidePulls}
          hidePercents={hidePercents}
          hideBossIcons={safeParseInt(qParams.hide_boss_icons as string, 0) !== 0}
          qParams={qParams}
        />
      );
    });

    const chunkedWidgets = _.chunk(widgets, itemsPerRow);

    chunkedWidgets.forEach((chunk: any, chunkIndex: number) => {
      const totalItems = chunk.length;

      const cols: ReactNode[] = [];

      for (let i = 0; i < totalItems; i++) {
        cols.push(
          <Col
            key={`${i}_col`}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {chunk[i]}
          </Col>
        );

        if (i < totalItems - 1 && safeParseInt(String(qParams.hide_vs)) === 0) {
          cols.push(
            <Col
              className="text-bold versus-widget-versus versus-widget-versus--no-padding"
              key={`${i}_vs`}
              style={{ position: 'relative', top: '-1rem', flex: '0 0 auto' }}
            >
              vs
            </Col>
          );
        }
      }

      parts.push(
        <Row
          align="space-around"
          gutter={{ md: 'small', smm: 'xx-small', xs: 'xxx-small' }}
          wrap={false}
          verticalAlign="center"
          key={`${chunkIndex}_row`}
        >
          {cols}
        </Row>
      );
    });

    const result: ReactNode[] = [];

    parts.forEach((part, index) => {
      if (index !== 0 || (itemsPerRow === 1 && index !== 0)) {
        result.push(
          <Col
            className="text-bold versus-widget-versus versus-widget-versus--no-padding slds-text-align--center"
            key={`${index}_vs`}
            style={{ position: 'relative', top: 0 }}
          >
            vs
          </Col>
        );
      }

      result.push(
        <Row
          align="space-around"
          gutter={{ md: 'small', smm: 'xx-small', xs: 'xxx-small' }}
          wrap={false}
          verticalAlign="center"
          key={`${index}_row`}
        >
          {part}
        </Row>
      );
    });

    return result;
  };

  return (
    <div
      className={cx(
        'versus-widget widget-container widget-anim--fade-in rio-text-shadow--normal slds-grid slds-grid--vertical slds-grid--vertical-align-center slds-grid--align-center rio-no-select',
        { [`widget-theme-${qParams.theme}`]: qParams.theme, ['widget-theme-default']: !qParams.theme }
      )}
      style={
        qParams.chromargb
          ? {
              backgroundImage: 'none',
              backgroundColor: qParams.chromargb === 'transparent' ? 'transparent' : qParams.chromargb.toString()
            }
          : {}
      }
    >
      {safeParseInt(qParams.hide_boss_icons as string, 0) !== 0 && (
        <>
          <div className="slds-col">
            <div className="versus-widget-guild-name">{data.guilds[0]?.latestEncounter.boss.name}</div>
          </div>
        </>
      )}

      <div
        className={cx('slds-col', { 'slds-p-horizontal--large': data.guilds.length > 3 })}
        style={data.guilds.length > 3 ? { width: '100%' } : {}}
      >
        <MediaV2 greaterThanOrEqual="lg">{getItems(4)}</MediaV2>
        <MediaV2 between={['smm', 'lg']}>{getItems(3)}</MediaV2>
        <MediaV2 lessThan="smm">{getItems(2)}</MediaV2>
      </div>
      {!qParams.hide_logo && (
        <div className="slds-col versus-powered-by">
          Powered By <img src={asset('/images/brand/Logo_White.svg')} />
        </div>
      )}
    </div>
  );
}

export default StreamWidgetVersusPage;
